import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as schedulesApi from '../../api/schedules';

// Async thunks
export const fetchAgentScheduleThunk = createAsyncThunk(
  'schedules/fetchAgentSchedule',
  async (agentId, { rejectWithValue }) => {
    try {
      return await schedulesApi.fetchAgentSchedule(agentId);
    } catch (error) {
      return rejectWithValue(error.message || `Failed to fetch schedule for agent ID ${agentId}`);
    }
  }
);

export const updateAgentScheduleThunk = createAsyncThunk(
  'schedules/updateAgentSchedule',
  async ({ agentId, scheduleData }, { rejectWithValue }) => {
    try {
      const result = await schedulesApi.updateAgentSchedule(agentId, scheduleData);
      return { agentId, scheduleData, result };
    } catch (error) {
      return rejectWithValue(error.message || `Failed to update schedule for agent ID ${agentId}`);
    }
  }
);

export const addScheduleEntryThunk = createAsyncThunk(
  'schedules/addEntry',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const result = await schedulesApi.addScheduleEntry(data);

      // Refresh the agent's schedule after adding an entry
      if (data.agent_id) {
        dispatch(fetchAgentScheduleThunk(data.agent_id));
      }

      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add schedule entry');
    }
  }
);

export const deleteScheduleEntryThunk = createAsyncThunk(
  'schedules/deleteEntry',
  async ({ id, agentId }, { rejectWithValue, dispatch }) => {
    try {
      const result = await schedulesApi.deleteScheduleEntry(id);

      // Refresh the agent's schedule after deleting an entry
      if (agentId) {
        dispatch(fetchAgentScheduleThunk(agentId));
      }

      return { id, result };
    } catch (error) {
      return rejectWithValue(error.message || `Failed to delete schedule entry ID ${id}`);
    }
  }
);

export const checkAgentWorkingStatusThunk = createAsyncThunk(
  'schedules/checkWorkingStatus',
  async ({ agentId, dateTime }, { rejectWithValue }) => {
    try {
      return await schedulesApi.checkAgentWorkingStatus(agentId, dateTime);
    } catch (error) {
      return rejectWithValue(error.message || `Failed to check working status for agent ID ${agentId}`);
    }
  }
);

// Initial state
const initialState = {
  schedule: [],
  currentAgentSchedule: null,
  workingStatus: null,
  loading: false,
  error: null,
  lastFetched: null
};

// Schedules slice
const schedulesSlice = createSlice({
  name: 'schedules',
  initialState,
  reducers: {
    clearScheduleErrors: (state) => {
      state.error = null;
    },
    setCurrentAgentSchedule: (state, action) => {
      state.currentAgentSchedule = action.payload;
      state.lastFetched = new Date().toISOString();
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch agent schedule
      .addCase(fetchAgentScheduleThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgentScheduleThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAgentSchedule = action.payload;
        state.lastFetched = new Date().toISOString();
      })
      .addCase(fetchAgentScheduleThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update agent schedule
      .addCase(updateAgentScheduleThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAgentScheduleThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAgentSchedule = action.payload.scheduleData;
        state.lastFetched = new Date().toISOString();
      })
      .addCase(updateAgentScheduleThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add schedule entry
      .addCase(addScheduleEntryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addScheduleEntryThunk.fulfilled, (state) => {
        state.loading = false;
        // We'll refresh the full schedule via the fetchAgentScheduleThunk after this action
      })
      .addCase(addScheduleEntryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete schedule entry
      .addCase(deleteScheduleEntryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteScheduleEntryThunk.fulfilled, (state) => {
        state.loading = false;
        // We'll refresh the full schedule via the fetchAgentScheduleThunk after this action
      })
      .addCase(deleteScheduleEntryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Check agent working status
      .addCase(checkAgentWorkingStatusThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAgentWorkingStatusThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.workingStatus = action.payload;
      })
      .addCase(checkAgentWorkingStatusThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Export actions
export const { clearScheduleErrors, setCurrentAgentSchedule } = schedulesSlice.actions;

// Selectors
export const selectCurrentAgentSchedule = (state) => state.schedules.currentAgentSchedule;
export const selectWorkingStatus = (state) => state.schedules.workingStatus;
export const selectSchedulesLoading = (state) => state.schedules.loading;
export const selectSchedulesError = (state) => state.schedules.error;
export const selectSchedulesLastFetched = (state) => state.schedules.lastFetched;

export default schedulesSlice.reducer;
